/**
 * Type-safe helpers for Supabase data handling
 * These functions provide type safety while handling Supabase responses
 */

import type { PostgrestError } from '@supabase/supabase-js';

/**
 * Type guard to check if a value is a PostgrestError
 */
export function isPostgrestError(error: unknown): error is PostgrestError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'code' in error
  );
}

/**
 * Type-safe data handler for Supabase query results
 * Replaces the old universalTypeFix pattern with proper typing
 */
export function handleSupabaseData<T>(data: T | null): T | undefined {
  return data ?? undefined;
}

/**
 * Type-safe array handler for Supabase query results
 * Ensures arrays are never null
 */
export function handleSupabaseArray<T>(data: T[] | null): T[] {
  return data ?? [];
}

/**
 * Type-safe single result handler for Supabase query results
 * Throws if no data is returned when it's required
 */
export function handleRequiredSupabaseData<T>(
  data: T | null,
  errorMessage = 'Required data not found',
): T {
  if (data === null || data === undefined) {
    throw new Error(errorMessage);
  }
  return data;
}

/**
 * Type-safe wrapper for Supabase queries that handles errors
 * Returns undefined if there's an error or no data
 */
export function safeSupabaseQuery<T>(
  data: T | null,
  error: PostgrestError | null,
): T | undefined {
  if (error) {
    throw error;
  }
  return data ?? undefined;
}

/**
 * Type-safe wrapper for Supabase array queries
 * Returns empty array if there's an error or no data
 */
export function safeSupabaseArrayQuery<T>(
  data: T[] | null,
  error: PostgrestError | null,
): T[] {
  if (error) {
    throw error;
  }
  return data ?? [];
}
